import axios, { AxiosHeaders, AxiosRequestConfig } from 'axios';
import { AuthModel } from '../services/authService';
import { notifySessionEnded } from './sessionEvents';
import { StorageKey, XStorage } from './storage';
import { refreshAfterUnauthorized } from './tokenRefresh';
import { decideStepUpAfterError, getStepUpToken, STEP_UP_HEADER } from './stepUp';
import { isStepUpRoute } from './stepUpRoutes';

/**
 * XBIT-4826 D10.2: what to do with a request the service answered with an error.
 * Returns the access token to resend the request with, or null to reject the original error.
 */

/** Routes whose `401` is about credentials being entered, or is the refresh itself: never refreshed. */
const SKIPPED_URL = /\/auth\/(refresh-token|login|2fa(\/|\?|$)|google)/;
const REFRESH_CODES = ['TOKEN_INVALID', 'ACCESS_TOKEN_SUPERSEDED'];

export interface FailedRequest {
  url?: string;
  /** The access token the request was sent with. */
  bearer?: string | null;
  /** True when this request is already the retry. */
  retried?: boolean;
}

type RetryableConfig = AxiosRequestConfig & {
  _authRetried?: boolean;
  _skipAuthRetry?: boolean;
  /** XBIT-4777: the call never opens the step-up dialog (a chain filter that fills itself only when a grant is held). */
  _skipStepUp?: boolean;
  /** XBIT-4777: this request is already the re-send after a step-up. */
  _stepUpRetried?: boolean;
};

export function bearerOf(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const match = /^Bearer\s+(.+)$/i.exec(header.trim());
  return match ? match[1] : null;
}

function readHeader(config: RetryableConfig | undefined, name: string): unknown {
  const headers = config?.headers as any;
  if (!headers) return undefined;
  return typeof headers.get === 'function' ? headers.get(name) : headers[name];
}

function describe(error: unknown, ctx: FailedRequest): { status?: number; code?: unknown; request: FailedRequest; skip: boolean } {
  if (axios.isAxiosError(error)) {
    const config = error.config as RetryableConfig | undefined;
    return {
      status: error.response?.status,
      code: (error.response?.data as { code?: unknown } | undefined)?.code,
      request: {
        url: ctx.url ?? config?.url,
        bearer: ctx.bearer ?? bearerOf(readHeader(config, 'Authorization')),
        retried: ctx.retried ?? !!config?._authRetried,
      },
      skip: !!config?._skipAuthRetry,
    };
  }
  // react-admin's HttpError: status and body, nothing about the request, which the caller passes in.
  const http = error as { status?: number; body?: { code?: unknown } } | null;
  return { status: http?.status, code: http?.body?.code, request: ctx, skip: false };
}

export async function decideAfterError(error: unknown, ctx: FailedRequest = {}): Promise<string | null> {
  const { status, code, request, skip } = describe(error, ctx);
  if (status !== 401 || skip || request.retried) return null;
  if (request.url && SKIPPED_URL.test(request.url)) return null;
  const bearer = request.bearer;
  if (!bearer) return null;

  if (typeof code === 'string' && REFRESH_CODES.includes(code)) {
    const pair = await refreshAfterUnauthorized(bearer);
    return pair?.accessToken && pair.accessToken !== bearer ? pair.accessToken : null;
  }
  if (code === 'SESSION_REVOKED') {
    const stored = await XStorage.get<AuthModel>(StorageKey.AuthData);
    if (stored?.accessToken === bearer) {
      await XStorage.remove(StorageKey.AuthData);
      notifySessionEnded();
    }
  }
  return null;
}

let installed = false;

/**
 * Registers, once, on the default axios instance: the step-up header on custody requests (XBIT-4777), and the
 * refresh-and-retry and step-up-and-retry of a refused response.
 */
export function installAuthInterceptor(): void {
  if (installed) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    const token = getStepUpToken();
    // Only ever adds: a header the step-up retry already set is left as it is.
    if (token && isStepUpRoute(config.method, config.url)) {
      const headers = AxiosHeaders.from(config.headers as any);
      if (!headers.has(STEP_UP_HEADER)) {
        headers.set(STEP_UP_HEADER, token);
        config.headers = headers;
      }
    }
    return config;
  });
  axios.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!axios.isAxiosError(error) || !error.config) throw error;
      const next = await decideAfterError(error);
      if (next) {
        const headers = AxiosHeaders.from(error.config.headers as any);
        headers.set('Authorization', `Bearer ${next}`);
        const retry: RetryableConfig = { ...error.config, headers, _authRetried: true };
        return axios.request(retry);
      }
      const config = error.config as RetryableConfig;
      const sent = readHeader(config, STEP_UP_HEADER);
      const token = await decideStepUpAfterError(error, {
        url: config.url,
        retried: !!config._stepUpRetried,
        skipStepUp: !!config._skipStepUp,
        sentToken: typeof sent === 'string' && sent ? sent : null,
      });
      if (!token) throw error;
      const headers = AxiosHeaders.from(config.headers as any);
      headers.set(STEP_UP_HEADER, token);
      const retry: RetryableConfig = { ...config, headers, _stepUpRetried: true };
      return axios.request(retry);
    }
  );
}
